/**
 * FilterDropdown Component
 *
 * Renders a dropdown menu for selecting filters or sub-options.
 * Uses MUI Popper for positioning and Fade for animation.
 */

"use client";

import React from "react";
import {
  Paper,
  Popper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Fade,
  Divider,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import type { FilterOption, SubOption, DropdownMode } from "../types";
import { DROPDOWN_TRANSITION_DURATION, DROPDOWN_Z_INDEX } from "../constants";

/**
 * Props for the FilterDropdown component
 */
interface FilterDropdownProps {
  /** Whether the dropdown is visible */
  open: boolean;
  /** Reference element for positioning */
  anchorEl: HTMLElement | null;
  /** Current dropdown display mode */
  mode: DropdownMode;
  /** Available filters to display (when mode is "filters") */
  availableFilters: FilterOption[];
  /** Current filter for sub-options display */
  currentFilter: FilterOption | null;
  /** Callback when a filter is selected */
  onFilterSelect: (filter: FilterOption) => void;
  /** Callback when a sub-option is selected */
  onSubOptionSelect: (subOption: SubOption) => void;
  /** Callback to go back to filter list from sub-options */
  onBackToFilters: () => void;
}

/**
 * FilterDropdown displays a positioned menu for filter/sub-option selection.
 *
 * @param props - Component props
 * @returns Rendered dropdown element
 *
 * @example
 * <FilterDropdown
 *   open={showDropdown}
 *   anchorEl={containerRef.current}
 *   mode="filters"
 *   availableFilters={filters}
 *   currentFilter={null}
 *   onFilterSelect={handleFilterSelect}
 *   onSubOptionSelect={handleSubOptionSelect}
 *   onBackToFilters={handleBack}
 * />
 */
export function FilterDropdown({
  open,
  anchorEl,
  mode,
  availableFilters,
  currentFilter,
  onFilterSelect,
  onSubOptionSelect,
  onBackToFilters,
}: FilterDropdownProps) {
  // Determine if dropdown should be shown
  const shouldShow =
    open && (mode === "filters" ? availableFilters.length > 0 : Boolean(currentFilter?.subOptions));

  return (
    <Popper
      open={shouldShow}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      style={{ zIndex: DROPDOWN_Z_INDEX, width: anchorEl?.offsetWidth }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={DROPDOWN_TRANSITION_DURATION}>
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {mode === "filters" ? (
              // ============================================
              // Filter List View
              // ============================================
              <>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 2,
                    py: 1,
                    bgcolor: "grey.50",
                    color: "text.secondary",
                    fontWeight: 600,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Select a filter
                </Typography>
                <List dense disablePadding>
                  {availableFilters.map((filter) => (
                    <FilterListItem key={filter.key} filter={filter} onSelect={onFilterSelect} />
                  ))}
                </List>
              </>
            ) : (
              // ============================================
              // Sub-Options View
              // ============================================
              <>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 2,
                    py: 1,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {currentFilter?.label}: Select condition
                </Typography>
                <List dense disablePadding>
                  {currentFilter?.subOptions?.map((subOption) => (
                    <SubOptionListItem
                      key={subOption.key}
                      subOption={subOption}
                      onSelect={onSubOptionSelect}
                    />
                  ))}
                </List>
                {/* Back button to return to filter list */}
                <Divider />
                <ListItemButton
                  onClick={onBackToFilters}
                  sx={{
                    py: 0.75,
                    px: 2,
                    bgcolor: "grey.50",
                    "&:hover": {
                      bgcolor: "grey.100",
                    },
                  }}
                >
                  <ListItemText
                    primary="Back to filters"
                    primaryTypographyProps={{
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  />
                </ListItemButton>
              </>
            )}
          </Paper>
        </Fade>
      )}
    </Popper>
  );
}

// ============================================
// Sub-components
// ============================================

/**
 * Individual filter list item with indicators for special types
 */
interface FilterListItemProps {
  filter: FilterOption;
  onSelect: (filter: FilterOption) => void;
}

function FilterListItem({ filter, onSelect }: FilterListItemProps) {
  // Determine secondary text based on filter type
  const getSecondaryText = () => {
    if (filter.subOptions) return "Has options";
    if (filter.type === "date") return "Date picker";
    return undefined;
  };

  return (
    <ListItemButton
      onClick={() => onSelect(filter)}
      sx={{
        py: 1,
        px: 2,
        "&:hover": {
          bgcolor: "primary.light",
          "& .MuiListItemText-primary": {
            color: "primary.contrastText",
          },
          "& .MuiListItemIcon-root": {
            color: "primary.contrastText",
          },
        },
      }}
    >
      <ListItemText
        primary={filter.label}
        secondary={getSecondaryText()}
        primaryTypographyProps={{
          fontWeight: 500,
          fontSize: 14,
        }}
        secondaryTypographyProps={{
          fontSize: 11,
          color: "text.disabled",
        }}
      />
      {/* Arrow icon for filters with sub-options */}
      {filter.subOptions && (
        <ListItemIcon sx={{ minWidth: 24, justifyContent: "flex-end" }}>
          <KeyboardArrowRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </ListItemIcon>
      )}
      {/* Calendar icon for date filters */}
      {filter.type === "date" && (
        <ListItemIcon sx={{ minWidth: 24, justifyContent: "flex-end" }}>
          <CalendarMonthIcon fontSize="small" sx={{ color: "info.main" }} />
        </ListItemIcon>
      )}
    </ListItemButton>
  );
}

/**
 * Individual sub-option list item
 */
interface SubOptionListItemProps {
  subOption: SubOption;
  onSelect: (subOption: SubOption) => void;
}

function SubOptionListItem({ subOption, onSelect }: SubOptionListItemProps) {
  return (
    <ListItemButton
      onClick={() => onSelect(subOption)}
      sx={{
        py: 1,
        px: 2,
        "&:hover": {
          bgcolor: "primary.light",
          "& .MuiListItemText-primary": {
            color: "primary.contrastText",
          },
        },
      }}
    >
      <ListItemText
        primary={subOption.label}
        primaryTypographyProps={{
          fontWeight: 500,
          fontSize: 14,
        }}
      />
    </ListItemButton>
  );
}
